import { useState, useEffect } from 'react';
import {
  GetFormsOrders,
  GetFormStatusList,
  GetFormsOrder,
  UpdateFormsOrderStatus,
} from '../../../api/forms';
import { useQuery } from '@tanstack/react-query';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Paper,
  Box,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LoadingIndicator from '../../../components/Global/loading-indicator/loading-indicator';

export default function MailRoom() {
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState(null);
  const [filteredOrders, setFilteredOrders] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [statusList, setStatusList] = useState(null);
  const [showCancel, setShowCancel] = useState(false);
  const [showComplete, setShowComplete] = useState(false);
  const [detailsID, setDetailsID] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [statusID, setStatusID] = useState(null);

  const getFormStatusListQuery = useQuery({
    queryKey: ['getFormStatusList', detailsID],
    queryFn: async () => await GetFormStatusList()(),
    gcTime: 0,
    enabled: false,
  });

  const getFormsOrdersQuery = useQuery({
    queryKey: ['getFormsOrders', detailsID],
    queryFn: async () => await GetFormsOrders()(),
    gcTime: 0,
    enabled: false,
  });

  // Handle getFormsOrdersQuery success
  useEffect(() => {
    if (getFormsOrdersQuery.isSuccess && getFormsOrdersQuery.data) {
      setOrders(JSON.parse(getFormsOrdersQuery.data));
      setLoading(false);
    }
  }, [getFormsOrdersQuery.isSuccess, getFormsOrdersQuery.data]);

  const getFormsOrderQuery = useQuery({
    queryKey: ['getFormsOrder', detailsID],
    queryFn: async () => await GetFormsOrder({ ID: detailsID, UpdateStatus: true })(),
    gcTime: 0,
    enabled: false,
  });

  useEffect(() => {
    setSelectedFilter('0');
    getFormStatusListQuery.refetch();
    getFormsOrdersQuery.refetch();
  }, []);

  useEffect(() => {
    if (getFormStatusListQuery.data != null) {
      setStatusList(JSON.parse(getFormStatusListQuery.data));
    }
  }, [getFormStatusListQuery.data]);

  useEffect(() => {
    if (selectedItem == null) {
      setLoading(true);
      getFormsOrdersQuery.refetch();
    }
  }, [selectedItem]);

  useEffect(() => {
    if (orders !== null) {
      if (selectedFilter === '0') {
        setFilteredOrders(orders);
      } else {
        setFilteredOrders(orders.filter((f) => f.StatusID.toString() === selectedFilter));
      }
    }
  }, [selectedFilter, orders]);

  const handleShowCancel = () => setShowCancel(true);
  const handleHideCancel = () => {
    setCancelReason('');
    setShowCancel(false);
  };
  const handleShowComplete = () => setShowComplete(true);
  const handleHideComplete = () => setShowComplete(false);

  const updateFormsOrderStatus = useQuery({
    queryKey: ['updateFormsOrderStatus', selectedItem?.ID || 0, statusID, cancelReason],
    queryFn: async () =>
      await UpdateFormsOrderStatus({
        OrderID: selectedItem?.ID || 0,
        StatusID: statusID,
        CancelReason: cancelReason,
      })(),
    enabled: false,
  });

  // Handle updateFormsOrderStatus success
  useEffect(() => {
    if (updateFormsOrderStatus.isSuccess) {
      setStatusID(null);
      setDetailsID(null);
      setCancelReason('');
      handleHideCancel();
      handleHideComplete();
      setSelectedItem(null);
    }
  }, [updateFormsOrderStatus.isSuccess]);

  // Handle updateFormsOrderStatus error
  useEffect(() => {
    if (updateFormsOrderStatus.isError) {
      setStatusID(null);
    }
  }, [updateFormsOrderStatus.isError]);

  useEffect(() => {
    if (statusID != null) updateFormsOrderStatus.refetch();
  }, [statusID]);

  const ConfirmModal = (v) => {
    setStatusID(v);
  };

  const DisplayForm = () => {
    return (
      <div className="container">
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => {
              setSelectedItem(null);
              setDetailsID(null);
            }}
          >
            Back to List
          </Button>
          {selectedItem !== null && selectedItem.StatusID < 3 ? (
            <Stack direction={'row'} gap={2}>
              <Button variant="contained" disableElevation onClick={handleShowComplete}>
                Complete Order
              </Button>
              <Button variant="outlined" onClick={handleShowCancel} color="error">
                Cancel Order
              </Button>
            </Stack>
          ) : (
            ''
          )}
        </Box>
        <hr />
        {selectedItem !== null ? (
          <div className="row">
            <div className="col">
              <div className="form-group">
                <label>Order ID:</label>
                <span className="form-control">{selectedItem.ID}</span>
              </div>
              <div className="form-group">
                <label>Agent Name:</label>
                <span className="form-control">{selectedItem.SendTo}</span>
              </div>
              <div className="form-group">
                <label>Producer ID:</label>
                <span className="form-control">{selectedItem.ProducerID}</span>
              </div>
              <div className="form-group">
                <label>Status:</label>
                <span className="form-control">{selectedItem.SendTo}</span>
              </div>
              <div className="form-group">
                <label>Send To Name:</label>
                <span className="form-control">{selectedItem.StatusName}</span>
              </div>
              <div className="form-group">
                <label>Address Information:</label>
                <span className="form-control">{selectedItem.Address1}</span>
                <span className="form-control">{selectedItem.Address2}</span>
                <span className="form-control">
                  {selectedItem.City}, {selectedItem.State} {selectedItem.Zip}
                </span>
              </div>
              <div className="form-group">
                <label>Email Address:</label>
                <span className="form-control">{selectedItem.EmailAddress}</span>
              </div>
              <div className="form-group">
                <label>Order Date:</label>
                <span className="form-control">{selectedItem.OrderDate}</span>
              </div>
              <div className="form-group">
                <label>Date Completed:</label>
                <span className="form-control">{selectedItem.DateCompleted}</span>
              </div>
            </div>
            <div className="col">
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Form Number</TableCell>
                      <TableCell>Form Name</TableCell>
                      <TableCell>Quantity</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {selectedItem.Forms !== null &&
                      selectedItem.Forms.map((e, i) => (
                        <TableRow key={'row' + i}>
                          <TableCell>{e.FormNumber}</TableCell>
                          <TableCell>{e.FormName}</TableCell>
                          <TableCell>{e.Quantity}</TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          </div>
        ) : (
          <div className="row">Nothing is selected</div>
        )}
        <Dialog
          open={showCancel}
          onClose={handleHideCancel}
          disableEscapeKeyDown
          sx={{ padding: '1rem' }}
        >
          <DialogTitle>Cancel Confirmation</DialogTitle>
          <DialogContent>
            <div>Are you sure you want to cancel this item?</div>
            <TextField
              id={'CancelReason ' + selectedItem?.ID}
              placeholder="Reason for Cancelling"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              key={'CancelReason ' + selectedItem?.ID}
              multiline
              maxRows={3}
              fullWidth
              margin="normal"
            />
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              color="primary"
              onClick={() => ConfirmModal(4)}
              disabled={!cancelReason}
            >
              Confirm
            </Button>
            <Button variant="outlined" onClick={handleHideCancel}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={showComplete}
          onClose={handleHideComplete}
          disableEscapeKeyDown
          sx={{ padding: '1rem' }}
        >
          <DialogTitle>Complete Confirmation</DialogTitle>
          <DialogContent>Are you sure you want to complete this item?</DialogContent>
          <DialogActions>
            <Button variant="contained" color="primary" onClick={() => ConfirmModal(3)}>
              Confirm
            </Button>
            <Button variant="outlined" onClick={handleHideComplete}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  };

  const DisplayTable = () => {
    return (
      <div className="container">
        <div className="row">
          <div className="col"></div>
        </div>
        <div className="row">
          <div className="col text-right">
            <span style={shortSpan}>
              Filter:&nbsp;
              <select
                id="mailRoomFilter"
                className="form-control"
                style={{ display: 'inline', width: '200px' }}
                onChange={(e) => setSelectedFilter(e.target.value)}
              >
                {statusList === null
                  ? ''
                  : statusList.map((e, i) => {
                      return (
                        <option key={'opt' + i} value={e.ID}>
                          {e.StatusName}
                        </option>
                      );
                    })}
              </select>
            </span>
          </div>
        </div>
        {filteredOrders !== null && (
          <TableContainer sx={{ marginTop: '1rem' }} component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Order Number</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Order Date</TableCell>
                  <TableCell>Date Completed</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredOrders.map((e) => (
                  <TableRow
                    key={e.ID}
                    hover
                    onClick={() => showDetails(e.ID)}
                    style={{ cursor: 'pointer' }}
                  >
                    <TableCell>{e.ID}</TableCell>
                    <TableCell>{e.StatusName}</TableCell>
                    <TableCell>{e.OrderDate}</TableCell>
                    <TableCell>{e.DateCompleted}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </div>
    );
  };

  const showDetails = (id) => {
    if (id === detailsID) getFormsOrderQuery.refetch();
    else setDetailsID(id);
  };

  useEffect(() => {
    if (detailsID != null) {
      getFormsOrderQuery.refetch();
    }
  }, [detailsID]);

  useEffect(() => {
    if (getFormsOrderQuery.data != null && detailsID != null && selectedItem == null) {
      setSelectedItem(JSON.parse(getFormsOrderQuery.data));
    }
  }, [getFormsOrderQuery.data, detailsID, selectedItem]);

  const shortSpan = {
    width: '250px',
    display: 'inline',
  };

  return (
    <>{loading ? <LoadingIndicator /> : selectedItem === null ? DisplayTable() : DisplayForm()}</>
  );
}
